import { useContext, useMemo } from 'react'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { UserPermissionsContext } from 'app/hooks/useUserPermission'
import type { UserJson } from 'app/types/entities'
import { debug } from 'lib/logging'
import { NavItemType } from 'types/Sidebar'

/**
 * Hook that filters sidebar items based on user permissions.
 * Returns empty items while permissions are loading to prevent flash of unauthorized pages.
 */
export function usePermissionFilteredSidebar(navItems: NavItemType[], user: UserJson) {
  const { permissions } = useContext(UserPermissionsContext)
  const checkPermission = useCheckPermission()

  const isLoading = user.type !== `super` && permissions === undefined

  const items = useMemo(() => {
    if (isLoading) {
      debug(`permissions`, `usePermissionFilteredSidebar: Permissions not loaded, returning empty array`)
      return []
    }

    debug(`permissions`, `usePermissionFilteredSidebar: Filtering nav items`, {
      totalItems: navItems.length,
      userPermissions: permissions,
      userType: user.type,
      roleId: user.roleId,
    })

    const hasPermissionForItem = (item: NavItemType): boolean => {
      if (!item.permission) return true
      return checkPermission(item.permission)
    }

    const filtered = navItems
      .map((item) => {
        if (item.children && item.children.length > 0) {
          if (item.permission) {
            const hasItemPermission = hasPermissionForItem(item)
            debug(`permissions`, `Nav item requires "${item.permission}" - result: ${hasItemPermission}`)
            if (!hasItemPermission) {
              return null
            }
          }

          const filteredChildren = item.children.filter((child) => {
            if (child.if === false) return false
            if (child.superUserOnly && user.type !== `super`) return false
            const hasChildPermission = hasPermissionForItem(child)
            debug(`permissions`, `Nav child item requires "${child.permission}" - result: ${hasChildPermission}`)
            return hasChildPermission
          })

          if (filteredChildren.length === 0) {
            debug(`permissions`, `Nav item hidden - no visible children`)
            return null
          }

          return {
            ...item,
            children: filteredChildren,
          }
        }

        const hasItemPermission = hasPermissionForItem(item)
        debug(`permissions`, `Nav item requires "${item.permission}" - result: ${hasItemPermission}`)
        return hasItemPermission ? item : null
      })
      .filter((item): item is NavItemType => item !== null)

    debug(
      `permissions`,
      `usePermissionFilteredSidebar: Filtered ${navItems.length} items down to ${filtered.length}`,
      filtered.map((item) => item.name),
    )

    return filtered
  }, [navItems, user, permissions, isLoading])

  return { items, isLoading }
}
